import { forwardRef, useId } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { cn } from '@/lib/cn';
import {
  fieldErrorClass,
  fieldHintClass,
  fieldInputClass,
  fieldLabelClass,
  slugifyLabel,
} from '@/components/forms/fieldStyles';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label: string;
  options: SelectOption[];
  error?: string;
  hint?: string;
  showLabel?: boolean;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    options,
    error,
    hint,
    id,
    className,
    showLabel = true,
    required,
    placeholder,
    value,
    defaultValue,
    ...props
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? props.name ?? `${slugifyLabel(label)}-${autoId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const hasPlaceholder = placeholder !== undefined;
  const showPlaceholder =
    hasPlaceholder &&
    (value === '' || value === undefined) &&
    (defaultValue === '' || defaultValue === undefined);

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className={cn(fieldLabelClass, !showLabel && 'sr-only')}>
        {label}
        {required ? <span className="text-red-400"> *</span> : null}
      </label>
      <div className="relative">
        <select
          ref={ref}
          id={inputId}
          required={required}
          value={value}
          defaultValue={defaultValue}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            fieldInputClass(Boolean(error), className),
            'appearance-none pr-10',
            showPlaceholder && 'text-[var(--text-muted)]',
          )}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled={required} className="bg-white text-slate-900">
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="bg-white text-slate-900"
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--text-muted)]"
          aria-hidden="true"
        />
      </div>
      {hint && !error ? (
        <p id={hintId} className={fieldHintClass}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={fieldErrorClass} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
